import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import {setFirmwareVersion, setModel, transceive} from './actions';
import {useHistory} from 'react-router-dom';
import Typography from "@material-ui/core/Typography";
import {hexStringToString, byteToHexString} from "./util";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Button from "@material-ui/core/Button";
import {useSnackbar} from "notistack";
import Grid from "@material-ui/core/Grid";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import {useTranslation} from "react-i18next";
import WarningIcon from "@material-ui/icons/Warning";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(2), // 移动端: 16px
    paddingTop: theme.spacing(6), // 移动端: 48px
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(3),
      paddingTop: theme.spacing(6),
    },
    [theme.breakpoints.up('md')]: {
      paddingLeft: "10%",
      paddingRight: "10%",
      paddingTop: "50px",
    },
  },
  card: {
    height: 150,
  }
}));

export default function Overview() {
  const classes = useStyles();
  const device = useSelector(state => state.device);
  const model = useSelector(state => state.model);
  const firmwareVersion = useSelector(state => state.firmwareVersion);
  const dispatch = useDispatch();
  const [sn, setSn] = useState("Unknown");
  const [id, setId] = useState("Unknown");
  const [deviceInfoLoaded, setDeviceInfoLoaded] = useState(false);
  const history = useHistory();
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();

  // 首次使用引导对话框
  const [setupWizardDialogOpen, setSetupWizardDialogOpen] = useState(false);
  const [defaultPinChecked, setDefaultPinChecked] = useState(false);

  // 辅助函数：字符串转十六进制
  const stringToHex = (str) => {
    return byteToHexString(new TextEncoder().encode(str));
  };

  // 当设备断开时重置缓存
  useEffect(() => {
    if (device === null) {
      setDeviceInfoLoaded(false);
      setSn("Unknown");
      setId("Unknown");
    }
  }, [device]);

  useEffect(() => {
    (async () => {
      if (device !== null && !deviceInfoLoaded) {
        try {
          let res = await dispatch(transceive("00A4040005F000000000"));
          if (!res.endsWith("9000")) {
            return;
          }
          
          // 尝试使用新的 READ_ALL_INFO 命令 (0x33) - 一次性读取所有信息
          res = await dispatch(transceive("0033000000"));
          if (res.endsWith("9000") && res.length > 4) {
            // 解析 TLV 格式的响应: Tag(1字节) + Length(1字节) + Value(可变长度)
            // 注意：这里的 res 是十六进制字符串，每2个字符代表1个字节
            const data = res.substring(0, res.length - 4);
            let offset = 0;
            
            while (offset < data.length) {
              const tag = parseInt(data.substring(offset, offset + 2), 16);
              const length = parseInt(data.substring(offset + 2, offset + 4), 16);
              const valueHex = data.substring(offset + 4, offset + 4 + length * 2);
              offset += 4 + length * 2;
              
              console.log(`TLV: Tag=0x${tag.toString(16).padStart(2, '0')}, Length=${length}, Value=${valueHex}`);
              
              switch (tag) {
                case 0x01: // Firmware Version (字符串)
                  const version = hexStringToString(valueHex);
                  console.log(`  Firmware Version: "${version}"`);
                  dispatch(setFirmwareVersion(version));
                  break;
                case 0x02: // Model (字符串)
                  const modelStr = hexStringToString(valueHex);
                  console.log(`  Model: "${modelStr}"`);
                  dispatch(setModel(modelStr));
                  break;
                case 0x03: // Serial Number (hex字符串，保持原样)
                  if (!valueHex || /^0+$/.test(valueHex)) {
                    setSn("Not Set");
                  } else {
                    console.log(`  Serial Number: ${valueHex}`);
                    setSn(valueHex);
                  }
                  break;
                case 0x04: // Chip ID (hex字符串，保持原样)
                  if (!valueHex || valueHex.length === 0) {
                    setId("Not Available");
                  } else {
                    console.log(`  Chip ID: ${valueHex}`);
                    setId(valueHex);
                  }
                  break;
                default:
                  console.log(`  Unknown tag: 0x${tag.toString(16)}`);
                  break;
              }
            }
            console.log("Device info loaded via READ_ALL_INFO (1 request)");
            setDeviceInfoLoaded(true);
          } else {
            // 降级到旧方法（兼容老固件）- 需要 4 次请求
            console.log("READ_ALL_INFO not supported, falling back to 4 individual reads");
            res = await dispatch(transceive("0031000000"));
            if (res.endsWith("9000")) {
              let version = hexStringToString(res.substring(0, res.length - 4));
              dispatch(setFirmwareVersion(version));
            }
            res = await dispatch(transceive("0031010000"));
            if (res.endsWith("9000")) {
              let model = hexStringToString(res.substring(0, res.length - 4));
              dispatch(setModel(model));
            }
            res = await dispatch(transceive("0032000000"));
            if (res.endsWith("9000")) {
              let sn = res.substring(0, res.length - 4);
              if (!sn || /^0+$/.test(sn)) {
                setSn("Not Set");
              } else {
                setSn(sn);
              }
            }
            res = await dispatch(transceive("0032010000"));
            if (res.endsWith("9000")) {
              let id = res.substring(0, res.length - 4);
              if (!id || id.length === 0) {
                setId("Not Available");
              } else {
                setId(id);
              }
            }
            setDeviceInfoLoaded(true);
          }
        } catch (err) {
          enqueueSnackbar(`Failed to get GriffonOakey version: ${err}`, {variant: "error"});
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device]); // 只依赖 device，避免 deviceInfoLoaded 变化导致的重复执行

  // 检测设备是否使用默认PIN，并显示引导对话框
  useEffect(() => {
    const checkDefaultPin = async () => {
      if (!device || !deviceInfoLoaded || defaultPinChecked) return;

      try {
        console.log('Checking if device is using default PIN...');
        const defaultPin = '123456';
        const defaultPinHex = stringToHex(defaultPin);
        const lc = (defaultPinHex.length / 2).toString(16).padStart(2, '0');
        const verifyAPDU = `00200000${lc}${defaultPinHex}`;

        const response = await dispatch(transceive(verifyAPDU));
        const sw = response.slice(-4);

        setDefaultPinChecked(true);

        if (sw === '9000') {
          // 默认PIN验证成功，说明设备未初始化
          console.log('Device is using default PIN - showing setup wizard dialog');
          // 延迟1秒显示对话框，让用户先看到设备信息
          setTimeout(() => {
            setSetupWizardDialogOpen(true);
          }, 1000);
        } else {
          console.log('Device is not using default PIN, SW:', sw);
        }
      } catch (error) {
        console.error('Failed to check default PIN:', error);
      }
    };

    checkDefaultPin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device, deviceInfoLoaded]);

  const handleSetupWizardGo = () => {
    setSetupWizardDialogOpen(false);
    history.push('/setup-wizard');
  };

  const handleSetupWizardLater = () => {
    setSetupWizardDialogOpen(false);
    enqueueSnackbar(t('overview.setupLaterReminder'), { variant: 'warning' });
  };

  return (
    <div className={classes.root}>
      {/* 首次使用引导对话框 */}
      <Dialog
        open={setupWizardDialogOpen}
        onClose={handleSetupWizardLater}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon style={{ color: '#ff9800', marginRight: 8 }} />
            {t('overview.setupWizardTitle')}
          </div>
        </DialogTitle>
        <DialogContent>
          <Typography paragraph>
            {t('overview.setupWizardMessage')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('overview.setupWizardWarning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSetupWizardLater} color="default">
            {t('overview.setupLater')}
          </Button>
          <Button onClick={handleSetupWizardGo} color="primary" variant="contained">
            {t('overview.setupNow')}
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container justifyContent={"center"} spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h2">
                GriffonOakey Info
              </Typography>
              {device !== null ? (
                <>
                  <Typography>
                    Connected: Yes
                  </Typography>
                  <Typography>
                    Model: {model || 'Loading...'}
                  </Typography>
                  <Typography>
                    Serial Number: {sn}
                  </Typography>
                  <Typography>
                    Chip ID: {id}
                  </Typography>
                  <Typography>
                    Firmware Version: {firmwareVersion || 'Loading...'}
                  </Typography>
                </>
              ) : (
                <Typography>
                  Connected: No
                </Typography>
              )}
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                color="primary" 
                onClick={() => setDeviceInfoLoaded(false)}
                disabled={device === null}
              >
                Refresh Device Info
              </Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent className={classes.card}>
              <Typography variant="h4">
                Admin
              </Typography>
              <Typography>
                Admin applet manages your GriffonOakey.
              </Typography>
            </CardContent>
            <CardActions>
              <Button onClick={() => history.push('/admin')}>Go</Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent className={classes.card}>
              <Typography variant="h4">
                OATH
              </Typography>
              <Typography>
                GriffonOakey implements a custom OATH (Open AuTHentication) applet.
              </Typography>
            </CardContent>
            <CardActions>
              <Button onClick={() => history.push('/oath')}>Go</Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent className={classes.card}>
              <Typography variant="h4">
                OpenPGP
              </Typography>
              <Typography>
                GriffonOakey implements OpenPGP standard.
              </Typography>
            </CardContent>
            <CardActions>
              <Button onClick={() => history.push('/openpgp')}>Go</Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent className={classes.card}>
              <Typography variant="h4">
                PIV
              </Typography>
              <Typography>
                GriffonOakey implements PIV standard.
              </Typography>
            </CardContent>
            <CardActions>
              <Button onClick={() => history.push('/piv')}>Go</Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent className={classes.card}>
              <Typography variant="h4">
                Backup
              </Typography>
              <Typography>
                Backup and restore device data.
              </Typography>
            </CardContent>
            <CardActions>
              <Button onClick={() => history.push('/backup')}>Go</Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
}
